import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Trash2,
  GripVertical,
  AlertCircle,
  Pencil,
  Lock
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { formFieldApi } from "@/services/api";
import { FormSection, FormField, SelectOption } from "@/models";
import { 
  Alert, 
  AlertDescription
} from "@/components/ui/alert";
import SimpleAddFieldDialog from "./dialogs/SimpleAddFieldDialog";
import EditFieldDialog from "./dialogs/EditFieldDialog";
import { getFieldIcon } from "./utils/fieldIcons";

interface FormFieldComponentProps {
  section: FormSection;
  onFieldUpdated: () => void;
  isParentSubmitting?: boolean;
}

const FormFieldComponent = ({
  section,
  onFieldUpdated,
  isParentSubmitting = false,
}: FormFieldComponentProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  
  const fields = section?.form_fields || [];
  const sortedFields = [...fields].sort((a, b) => a.field_order - b.field_order);
  
  const handleDeleteField = async (fieldId: number) => {
    try {
      setIsSubmitting(true);
      setError(null);
      await formFieldApi.delete(fieldId);
      
      toast({
        title: "Field deleted",
        description: "The field has been deleted successfully",
      });
      
      await onFieldUpdated();
    } catch (error: any) {
      setError(error.message || "An error occurred while deleting the field");
      toast({
        variant: "destructive",
        title: "Error deleting field",
        description: error.message || "An error occurred while deleting the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditField = (field: FormField) => {
    console.log("Editing field:", field);
    setEditingField(field);
    setFieldDialogOpen(true);
  };
  
  const handleUpdateField = async (
    label: string, 
    fieldType: string,
    filledBy: string,
    isRequired: boolean,
    content?: string,
    options?: SelectOption[],
    name?: string,
    dataType?: string,
    mainApplicantOnly?: boolean
  ) => {
    if (!editingField) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      const updateData: any = {
        label: label || editingField.label,
        field_type: fieldType,
        filled_by: filledBy,
        is_required: isRequired,
        name: name || null,
        data_type: dataType || 'generic'
      };

      // Multi-applicant signatures (D1): send the flag only for the field types
      // it applies to, so it can never affect other types.
      if (fieldType === "signature" || fieldType === "initials") {
        updateData.main_applicant_only = mainApplicantOnly === true;
      }
      
      console.log("Updating field with data:", updateData);
      
      // Store options in content for select fields
      if (fieldType === "select" && options) {
        updateData.content = JSON.stringify(options);
        console.log("Adding options to content:", updateData.content);
      } else if (fieldType === "information" && content !== undefined) {
        updateData.content = content;
        console.log("Setting information content:", updateData.content);
      }
      
      console.log("Sending update with data:", updateData);
      await formFieldApi.update(editingField.id, updateData);
      
      toast({
        title: "Field updated",
        description: "The field has been updated successfully",
      });
      
      await onFieldUpdated();
      setFieldDialogOpen(false);
      setEditingField(null);
    } catch (error: any) {
      setError(error.message || "An error occurred while updating the field");
      toast({
        variant: "destructive",
        title: "Error updating field",
        description: error.message || "An error occurred while updating the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    
    if (!destination) {
      return;
    }
    
    if (destination.index === source.index) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      const reorderedFields = [...sortedFields];
      const [movedField] = reorderedFields.splice(source.index, 1);
      reorderedFields.splice(destination.index, 0, movedField);
      
      const updatePromises = reorderedFields.map((field, index) => {
        return formFieldApi.update(field.id, { field_order: index });
      });
      
      await Promise.all(updatePromises);
      
      toast({
        title: "Fields reordered",
        description: "The fields have been reordered successfully",
      });
      
      await onFieldUpdated();
    } catch (error: any) {
      setError(error.message || "An error occurred while reordering fields");
      toast({
        variant: "destructive",
        title: "Error reordering fields",
        description: error.message || "An error occurred while reordering fields",
      });
      console.error("Field drag and drop error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = isSubmitting || isParentSubmitting;
  
  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable 
          droppableId={`field-list-${section.id}`}
          direction="vertical"
        >
          {(provided) => (
            <div 
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-3"
            >
              {sortedFields.map((field, index) => {
                const fieldDraggableId = `field-${field.id}`;
                
                return (
                  <Draggable 
                    key={fieldDraggableId} 
                    draggableId={fieldDraggableId} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`p-3 border ${field.is_required ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'} rounded-md flex items-center justify-between ${snapshot.isDragging ? 'opacity-80 border-primary shadow-md' : ''}`}
                      >
                        <div className="flex items-center flex-1">
                          <div 
                            {...provided.dragHandleProps} 
                            className="cursor-grab active:cursor-grabbing mr-2"
                            aria-label="Drag to reorder field"
                          >
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                          </div>
                          
                          <div className="flex items-center">
                            {getFieldIcon(field.field_type)}
                            <span className="font-medium ml-2">
                              {field.label || `Field ${index + 1}`}
                            </span>
                            
                            {field.is_required && (
                              <span className="ml-2 bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded">
                                Required
                              </span>
                            )}
                            
                            <span className="ml-2 bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded">
                              {field.field_type ? field.field_type.charAt(0).toUpperCase() + field.field_type.slice(1) : 'Unknown'}
                            </span>

                            {field.filled_by && (
                              <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                                {field.filled_by.charAt(0).toUpperCase() + field.filled_by.slice(1)} Fills
                              </span>
                            )}
                            
                            {field.name && (
                              <span className="ml-2 bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded">
                                Name: {field.name}
                              </span>
                            )}

                            {field.data_type && field.data_type !== 'generic' && (
                              <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                                Type: {field.data_type}
                              </span>
                            )}
                            {(field.field_type === "signature" ||
                              field.field_type === "initials") && (
                              <span
                                className={
                                  field.main_applicant_only
                                    ? "ml-2 bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded"
                                    : "ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded"
                                }
                              >
                                {field.main_applicant_only
                                  ? "Only main applicant"
                                  : "All applicants sign"}
                              </span>
                            )}
                            {field.is_system_required && (
                              <span className="ml-2 bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded">
                                System
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex">
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() => handleEditField(field)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                            disabled={isDisabled}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          
                          {field.is_system_required ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground/60 cursor-not-allowed">
                                    <Lock className="h-4 w-4" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-xs">
                                  Required for applicant signatures — this field
                                  cannot be deleted. You can still reorder it.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={() => handleDeleteField(field.id)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive/90 hover:bg-background"
                              disabled={isDisabled}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      
      {sortedFields.length === 0 && (
        <div className="p-4 text-center border rounded-md text-muted-foreground">
          No fields found. Add fields to this section.
        </div>
      )}

      <EditFieldDialog
        field={editingField}
        isOpen={fieldDialogOpen}
        onClose={() => {
          setFieldDialogOpen(false);
          setEditingField(null);
        }}
        onUpdate={handleUpdateField}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default FormFieldComponent;
